/**
 * Hand-off between the NetSuite pricing widgets on the same page.
 *
 * The tier checker publishes what the reader told it (full users, monthly
 * transaction lines); the complexity game reads it to pre-fill answers the
 * reader hasn't touched yet. Stored in sessionStorage so it survives a reload
 * within the tab, and announced with a window event so a widget that's already
 * mounted can react. Every access is guarded: storage can throw in private
 * windows and embeds, and the widgets must work without it.
 *
 * Holds reader inputs only. Never put a price in here.
 */

export const HANDOFF_KEY = 'nsp-handoff-v1';
export const HANDOFF_EVENT = 'nsp-handoff';

export interface PricingHandoff {
  /** Full (General Access) user licences, as entered in the tier checker. */
  users?: number;
  /** Average monthly transaction lines, as computed or entered in the tier checker. */
  linesPerMonth?: number;
  /** Which widget wrote last, e.g. 'tier-checker'. */
  source?: string;
  updatedAt?: number;
}

export function readHandoff(): PricingHandoff {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as PricingHandoff) : {};
  } catch {
    return {};
  }
}

export function writeHandoff(patch: PricingHandoff): void {
  if (typeof window === 'undefined') return;
  const next: PricingHandoff = { ...readHandoff(), ...patch, updatedAt: Date.now() };
  try {
    window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: the event below still reaches mounted widgets */
  }
  window.dispatchEvent(new CustomEvent<PricingHandoff>(HANDOFF_EVENT, { detail: next }));
}

/** Subscribe to hand-off updates. Returns an unsubscribe function. */
export function onHandoff(cb: (h: PricingHandoff) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => cb((e as CustomEvent<PricingHandoff>).detail ?? readHandoff());
  window.addEventListener(HANDOFF_EVENT, handler);
  return () => window.removeEventListener(HANDOFF_EVENT, handler);
}
